/**
 * Control dimensional F1 — sección "Plano y cotas" de la ficha de una pieza.
 *
 * Tres cosas y nada más: el plano maestro, la tabla de cotas críticas y la política de
 * control. Medir al cerrar producción es F2 y no está acá.
 *
 * LA DECISIÓN QUE GOBIERNA ESTA PANTALLA: las propuestas de la IA se muestran APARTE de
 * las cotas guardadas, y se aceptan de a una. No hay ningún botón que las acepte todas
 * juntas, y no es un olvido — una cota mal leída de un plano no es un dato feo en una
 * tabla, es una pieza que se acepta o se rechaza contra una medida equivocada. Que haya
 * que mirar cada una es el punto.
 *
 * Cada propuesta muestra su CONFIANZA y la RAZÓN por la que el modelo la propone, porque
 * quien valida necesita saber de qué está más y de qué está menos seguro; una lista plana
 * parece toda igual de cierta y se acepta en bloque sin leerla.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";

import backend from "../../backend";

const COLOR = {
  panel: "#242424",
  borde: "#2e2e2e",
  campo: "#161616",
  bordeCampo: "#333",
  texto: "#ddd",
  tenue: "#8a8a8a",
  acento: "#e30613",
};

const cajaPanel = {
  background: COLOR.panel,
  border: `1px solid ${COLOR.borde}`,
  borderRadius: 8,
  padding: "14px 16px",
  marginBottom: 12,
};

const tituloPanel = {
  fontSize: 10,
  fontWeight: 700,
  color: COLOR.acento,
  textTransform: "uppercase",
  letterSpacing: ".07em",
  marginBottom: 10,
};

const campo = {
  background: COLOR.campo,
  border: `1px solid ${COLOR.bordeCampo}`,
  color: COLOR.texto,
  fontSize: 12,
};

const COLOR_CONFIANZA = { alta: "#4ade80", media: "#fbbf24", baja: "#f87171" };

/** Una cota vacía para agregar a mano. */
const cotaNueva = () => ({
  etiqueta: "",
  nominal: "",
  toleranciaMas: "",
  toleranciaMenos: "",
  unidad: "mm",
  critica: false,
  notas: "",
  origen: "humano",
});

/** El rango que resulta de una cota, para que se vea el intervalo real y no dos números. */
const rango = (c) => {
  const n = Number(c.nominal);
  if (!Number.isFinite(n)) return "—";
  const mas = Math.abs(Number(c.toleranciaMas) || 0);
  const menos = Math.abs(Number(c.toleranciaMenos) || 0);
  if (!mas && !menos) return `${n} ${c.unidad || "mm"}`;
  return `${(n - menos).toFixed(3).replace(/\.?0+$/, "")} … ${(n + mas)
    .toFixed(3)
    .replace(/\.?0+$/, "")} ${c.unidad || "mm"}`;
};

const PlanoYCotas = ({ pieceId, precio }) => {
  const [cotas, setCotas] = useState([]);
  const [politica, setPolitica] = useState({ modo: "muestreo", tamanoMuestra: 3 });
  const [sugerida, setSugerida] = useState(null);
  const [plano, setPlano] = useState(null);

  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [analizando, setAnalizando] = useState(false);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");

  const [propuestas, setPropuestas] = useState(null);
  const archivoRef = useRef();

  const cargar = useCallback(async () => {
    if (!pieceId) return;
    setCargando(true);
    setError("");
    try {
      const datos = (await backend.get(`control-dimensional/${pieceId}/cotas`)).data;
      setCotas(datos.cotasCriticas || []);
      setPolitica(datos.politicaControl || { modo: "muestreo", tamanoMuestra: 3 });
      setPlano(datos.planoMaestro?.archivo?.url ? datos.planoMaestro : null);
      const s = (await backend.get(`control-dimensional/${pieceId}/politica/sugerida`)).data;
      setSugerida(s);
    } catch (err) {
      setError(err.response?.data || err.message);
    } finally {
      setCargando(false);
    }
  }, [pieceId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const subirPlano = async (e) => {
    const archivo = e.target.files?.[0];
    if (!archivo) return;
    setSubiendo(true);
    setError("");
    setAviso("");
    try {
      const formData = new FormData();
      formData.append("plano", archivo);
      const nuevo = (await backend.post(`control-dimensional/${pieceId}/plano-maestro`, formData))
        .data;
      setPlano(nuevo);
      setAviso("Plano maestro guardado.");
    } catch (err) {
      setError(err.response?.data || err.message);
    } finally {
      setSubiendo(false);
      if (archivoRef.current) archivoRef.current.value = "";
    }
  };

  const quitarPlano = async () => {
    setError("");
    try {
      await backend.delete(`control-dimensional/${pieceId}/plano-maestro`);
      setPlano(null);
      setPropuestas(null);
    } catch (err) {
      setError(err.response?.data || err.message);
    }
  };

  const cambiarCota = (i, campoNombre, valor) => {
    setCotas((previas) =>
      previas.map((c, idx) => (idx === i ? { ...c, [campoNombre]: valor } : c))
    );
  };

  const guardarCotas = async () => {
    setGuardando(true);
    setError("");
    setAviso("");
    try {
      const guardadas = (
        await backend.put(`control-dimensional/${pieceId}/cotas`, { cotas })
      ).data;
      setCotas(guardadas);
      setAviso(`${guardadas.length} cota(s) guardadas.`);
    } catch (err) {
      setError(err.response?.data || err.message);
    } finally {
      setGuardando(false);
    }
  };

  const borrarCota = async (i) => {
    // Si la cota todavía no está en el servidor, basta con sacarla de la tabla.
    if (!cotas[i]?.creadaEn) {
      setCotas((previas) => previas.filter((_, idx) => idx !== i));
      return;
    }
    setError("");
    try {
      const quedan = (await backend.delete(`control-dimensional/${pieceId}/cotas/${i}`)).data;
      setCotas(quedan);
    } catch (err) {
      setError(err.response?.data || err.message);
    }
  };

  const guardarPolitica = async (modo, tamanoMuestra) => {
    setError("");
    try {
      const nueva = (
        await backend.put(`control-dimensional/${pieceId}/politica`, { modo, tamanoMuestra })
      ).data;
      setPolitica(nueva);
    } catch (err) {
      setError(err.response?.data || err.message);
    }
  };

  const proponer = async () => {
    setAnalizando(true);
    setError("");
    setAviso("");
    setPropuestas(null);
    try {
      const datos = (await backend.post(`control-dimensional/${pieceId}/proponer-cotas`, {})).data;
      setPropuestas(datos);
    } catch (err) {
      setError(err.response?.data || err.message);
    } finally {
      setAnalizando(false);
    }
  };

  /** Acepta UNA propuesta: la guarda con origen "ia" y la saca de la lista. */
  const aceptarPropuesta = async (i) => {
    const p = propuestas.cotas[i];
    setError("");
    try {
      const guardadas = (
        await backend.post(`control-dimensional/${pieceId}/cotas`, { ...p, origen: "ia" })
      ).data;
      setCotas(guardadas);
      setPropuestas((prev) => ({ ...prev, cotas: prev.cotas.filter((_, idx) => idx !== i) }));
    } catch (err) {
      setError(err.response?.data || err.message);
    }
  };

  const descartarPropuesta = (i) => {
    setPropuestas((prev) => ({ ...prev, cotas: prev.cotas.filter((_, idx) => idx !== i) }));
  };

  if (!pieceId) {
    return (
      <div style={cajaPanel}>
        <div style={tituloPanel}>Plano y cotas</div>
        <div style={{ color: COLOR.tenue, fontSize: 12 }}>
          Guarda la pieza primero: el plano maestro y sus cotas se cuelgan de una pieza que
          ya existe.
        </div>
      </div>
    );
  }

  return (
    <div style={cajaPanel}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={tituloPanel}>Plano y cotas · control dimensional</div>
        {cargando && <span style={{ color: COLOR.tenue, fontSize: 11 }}>Cargando…</span>}
      </div>

      {error && (
        <div
          role="alert"
          style={{
            background: "#2d0f0f",
            border: "1px solid #5c1f1f",
            color: "#f87171",
            borderRadius: 6,
            padding: "8px 10px",
            fontSize: 12,
            marginBottom: 10,
          }}
        >
          {String(error)}
        </div>
      )}
      {aviso && (
        <div style={{ color: "#4ade80", fontSize: 12, marginBottom: 10 }}>{aviso}</div>
      )}

      {/* ── Plano maestro ─────────────────────────────────────────────── */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: COLOR.tenue, marginBottom: 6 }}>
          Plano maestro — el plano ACOTADO contra el que se controla. Es independiente del
          plano de taller: actualizar uno no cambia el otro.
        </div>
        {plano?.archivo?.url ? (
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <a
              href={plano.archivo.url}
              target="_blank"
              rel="noreferrer"
              style={{ color: COLOR.texto, fontSize: 12 }}
            >
              <i className="bi bi-file-earmark-ruled" /> {plano.archivo.nombre || "Ver plano"}
            </a>
            <span style={{ color: COLOR.tenue, fontSize: 11 }}>
              {plano.subidoEn ? new Date(plano.subidoEn).toLocaleDateString("es-CL") : ""}
              {plano.subidoPor ? ` · ${plano.subidoPor}` : ""}
            </span>
            <Button size="sm" variant="outline-secondary" onClick={quitarPlano}>
              Quitar
            </Button>
          </div>
        ) : (
          <div style={{ color: COLOR.tenue, fontSize: 12 }}>Sin plano maestro.</div>
        )}
        <Form.Control
          ref={archivoRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp"
          onChange={subirPlano}
          disabled={subiendo}
          style={{ ...campo, marginTop: 8, maxWidth: 380 }}
        />
        {subiendo && <div style={{ color: COLOR.tenue, fontSize: 11 }}>Subiendo…</div>}
      </div>

      {/* ── Política de control ───────────────────────────────────────── */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: COLOR.tenue, marginBottom: 6 }}>
          Política de control
          {sugerida && (
            <>
              {" "}— por precio ({Number(sugerida.precio || 0).toLocaleString("es-CL")}) le
              corresponde{" "}
              <strong>
                {sugerida.modo === "cien_por_ciento" ? "100%" : "muestreo"}
              </strong>
              , porque el umbral es {Number(sugerida.umbral).toLocaleString("es-CL")}.
            </>
          )}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <Form.Select
            value={politica.modo || "muestreo"}
            onChange={(e) => guardarPolitica(e.target.value, politica.tamanoMuestra)}
            style={{ ...campo, maxWidth: 220 }}
          >
            <option value="cien_por_ciento">Medir el 100% de las unidades</option>
            <option value="muestreo">Medir una muestra</option>
          </Form.Select>
          {politica.modo === "muestreo" && (
            <>
              <span style={{ color: COLOR.tenue, fontSize: 12 }}>Tamaño de muestra</span>
              <Form.Control
                type="number"
                min={1}
                value={politica.tamanoMuestra ?? 3}
                onChange={(e) =>
                  setPolitica((p) => ({ ...p, tamanoMuestra: Number(e.target.value) }))
                }
                onBlur={(e) => guardarPolitica(politica.modo, Number(e.target.value))}
                style={{ ...campo, width: 90 }}
              />
            </>
          )}
        </div>
      </div>

      {/* ── Cotas críticas ────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: COLOR.tenue }}>
          Cotas críticas — qué se mide cuando la pieza está terminada.
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => setCotas((p) => [...p, cotaNueva()])}
          >
            + Cota
          </Button>
          <Button
            size="sm"
            variant="outline-light"
            onClick={proponer}
            disabled={analizando || !plano?.archivo?.url}
            title={
              plano?.archivo?.url
                ? "Lee el plano maestro y propone cotas para que las valides"
                : "Sube el plano maestro primero"
            }
          >
            {analizando ? "Leyendo el plano…" : "Proponer cotas con IA"}
          </Button>
          <Button size="sm" variant="danger" onClick={guardarCotas} disabled={guardando}>
            {guardando ? "Guardando…" : "Guardar cotas"}
          </Button>
        </div>
      </div>

      {cotas.length === 0 ? (
        <div style={{ color: COLOR.tenue, fontSize: 12, marginBottom: 10 }}>
          Esta pieza todavía no tiene cotas definidas.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 12, color: COLOR.texto, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ color: COLOR.tenue, fontSize: 10, textTransform: "uppercase" }}>
                <th style={{ textAlign: "left", padding: 6 }}>Etiqueta</th>
                <th style={{ textAlign: "right", padding: 6 }}>Nominal</th>
                <th style={{ textAlign: "right", padding: 6 }}>Tol. +</th>
                <th style={{ textAlign: "right", padding: 6 }}>Tol. −</th>
                <th style={{ textAlign: "left", padding: 6 }}>Unidad</th>
                <th style={{ textAlign: "left", padding: 6 }}>Rango</th>
                <th style={{ textAlign: "center", padding: 6 }}>Crítica</th>
                <th style={{ textAlign: "left", padding: 6 }}>Origen</th>
                <th style={{ padding: 6 }} />
              </tr>
            </thead>
            <tbody>
              {cotas.map((c, i) => (
                <tr key={`${c.etiqueta}-${i}`} style={{ borderTop: `1px solid ${COLOR.borde}` }}>
                  <td style={{ padding: 4 }}>
                    <Form.Control
                      value={c.etiqueta ?? ""}
                      onChange={(e) => cambiarCota(i, "etiqueta", e.target.value)}
                      style={{ ...campo, minWidth: 150 }}
                    />
                  </td>
                  <td style={{ padding: 4 }}>
                    <Form.Control
                      type="number"
                      step="any"
                      value={c.nominal ?? ""}
                      onChange={(e) => cambiarCota(i, "nominal", e.target.value)}
                      style={{ ...campo, width: 90, textAlign: "right" }}
                    />
                  </td>
                  <td style={{ padding: 4 }}>
                    <Form.Control
                      type="number"
                      step="any"
                      value={c.toleranciaMas ?? ""}
                      onChange={(e) => cambiarCota(i, "toleranciaMas", e.target.value)}
                      style={{ ...campo, width: 80, textAlign: "right" }}
                    />
                  </td>
                  <td style={{ padding: 4 }}>
                    <Form.Control
                      type="number"
                      step="any"
                      value={c.toleranciaMenos ?? ""}
                      onChange={(e) => cambiarCota(i, "toleranciaMenos", e.target.value)}
                      style={{ ...campo, width: 80, textAlign: "right" }}
                    />
                  </td>
                  <td style={{ padding: 4 }}>
                    <Form.Control
                      value={c.unidad ?? "mm"}
                      onChange={(e) => cambiarCota(i, "unidad", e.target.value)}
                      style={{ ...campo, width: 70 }}
                    />
                  </td>
                  <td style={{ padding: 6, color: COLOR.tenue, whiteSpace: "nowrap" }}>{rango(c)}</td>
                  <td style={{ padding: 6, textAlign: "center" }}>
                    <Form.Check
                      type="checkbox"
                      checked={Boolean(c.critica)}
                      onChange={(e) => cambiarCota(i, "critica", e.target.checked)}
                    />
                  </td>
                  <td style={{ padding: 6, color: COLOR.tenue }}>
                    {c.origen === "ia" ? "IA validada" : "manual"}
                  </td>
                  <td style={{ padding: 4, textAlign: "right" }}>
                    <Button size="sm" variant="outline-danger" onClick={() => borrarCota(i)}>
                      ×
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Propuestas de la IA ───────────────────────────────────────── */}
      {propuestas && (
        <div
          style={{
            marginTop: 14,
            border: `1px dashed ${COLOR.bordeCampo}`,
            borderRadius: 8,
            padding: "10px 12px",
          }}
        >
          <div style={{ fontSize: 11, color: COLOR.tenue, marginBottom: 8 }}>
            {propuestas.aviso || "Propuestas: ninguna se guardó."} Revísalas y acepta las
            que correspondan, de a una.
          </div>

          {propuestas.observaciones && (
            <div style={{ fontSize: 12, color: "#fbbf24", marginBottom: 8 }}>
              Observación del modelo: {propuestas.observaciones}
            </div>
          )}

          {propuestas.cotas.length === 0 ? (
            <div style={{ color: COLOR.tenue, fontSize: 12 }}>
              No quedaron propuestas por revisar.
            </div>
          ) : (
            propuestas.cotas.map((p, i) => {
              const yaEsta = (propuestas.yaDefinidas || []).includes(p.etiqueta);
              return (
                <div
                  key={`${p.etiqueta}-${i}`}
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                    flexWrap: "wrap",
                    padding: "6px 0",
                    borderTop: `1px solid ${COLOR.borde}`,
                  }}
                >
                  <span
                    title={`Confianza ${p.confianza}`}
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 8,
                      background: COLOR_CONFIANZA[p.confianza] || COLOR.tenue,
                      flexShrink: 0,
                    }}
                  />
                  <strong style={{ fontSize: 12, color: COLOR.texto }}>{p.etiqueta}</strong>
                  <span style={{ fontSize: 12, color: COLOR.tenue }}>{rango(p)}</span>
                  {p.critica && (
                    <span style={{ fontSize: 10, color: COLOR.acento, fontWeight: 700 }}>CRÍTICA</span>
                  )}
                  {yaEsta && (
                    <span style={{ fontSize: 11, color: "#fbbf24" }}>
                      ya hay una cota con esta etiqueta
                    </span>
                  )}
                  {p.razon && (
                    <span style={{ fontSize: 11, color: COLOR.tenue, flex: 1, minWidth: 180 }}>
                      {p.razon}
                    </span>
                  )}
                  <Button size="sm" variant="outline-success" onClick={() => aceptarPropuesta(i)}>
                    Aceptar
                  </Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => descartarPropuesta(i)}>
                    Descartar
                  </Button>
                </div>
              );
            })
          )}

          {propuestas.descartadas?.length > 0 && (
            <div style={{ fontSize: 11, color: COLOR.tenue, marginTop: 8 }}>
              Se descartaron {propuestas.descartadas.length} propuesta(s) por venir sin
              nominal numérico: {propuestas.descartadas.map((d) => d.etiqueta).join(", ")}.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlanoYCotas;
